import React, { useEffect, useState } from "react";
import { Project } from "../../services/projectsApi";

interface AreaOption {
  id: string;
  name: string;
}

// Create / edit a project: a name + a membership picker over `areas`. Owns its
// transient form state (re-initialised each time it opens); the parent owns the
// open/saving flags and does the persistence via onSave.
const ProjectEditModal: React.FC<{
  open: boolean;
  project: Project | null; // null = create mode
  areas: AreaOption[];
  saving: boolean;
  onClose: () => void;
  onSave: (name: string, areaIds: string[]) => void;
}> = ({ open, project, areas, saving, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [areaIds, setAreaIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? "");
    setAreaIds(new Set(project?.areaIds ?? []));
    setSearch("");
  }, [open, project]);

  if (!open) return null;

  const filtered = areas.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()));
  const toggle = (id: string) =>
    setAreaIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-3xl bg-surface p-6 shadow-card-hover"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-xl font-bold text-ink mb-4">
          {project ? "Edit project" : "New project"}
        </h3>
        <label className="block text-xs font-mono uppercase tracking-widest text-ink-faint mb-1">
          Name
        </label>
        <input
          className="mb-4 w-full rounded-xl border border-paper-300 px-3 py-2 text-sm text-ink focus:border-brand-500 focus:outline-none"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Project name"
        />
        <div className="mb-1 flex items-center justify-between">
          <label className="block text-xs font-mono uppercase tracking-widest text-ink-faint">
            Areas
          </label>
          <span className="text-xs text-ink-faint">{areaIds.size} selected</span>
        </div>
        <input
          className="mb-2 w-full rounded-xl border border-paper-300 px-3 py-2 text-sm text-ink focus:border-brand-500 focus:outline-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search areas…"
        />
        <div className="max-h-64 divide-y divide-paper-100 overflow-auto rounded-xl border border-paper-200">
          {filtered.map((a) => (
            <label
              key={a.id}
              className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-paper-100"
            >
              <input
                type="checkbox"
                className="h-4 w-4 flex-shrink-0"
                checked={areaIds.has(a.id)}
                onChange={() => toggle(a.id)}
              />
              <span className="truncate text-sm text-ink">{a.name}</span>
            </label>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-ink-faint">No matching areas.</p>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center rounded-full border border-paper-300 px-4 py-2 text-sm font-medium text-ink-muted hover:bg-paper-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(name.trim(), Array.from(areaIds))}
            disabled={!name.trim() || saving}
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-4 py-2 text-sm font-semibold text-on-brand hover:bg-brand-600 transition-colors disabled:opacity-50"
          >
            {saving && (
              <div className="tf-spinner h-4 w-4 border-2 border-on-brand/30 border-t-on-brand" />
            )}
            {project ? "Save changes" : "Create project"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectEditModal;
